import { State } from '@/store/index';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import {
	BalanceAccount,
	BalanceAccountAutoTopUp,
	DbTimestamp,
	centsToDollars,
	currency,
	currencyTokens,
	hundredthsToTokens,
} from '@run-diffusion/shared';
import moment from 'moment-timezone';
import _get from 'lodash/get';

export const isWithinLimitNumRunnits: (state: State, runnitId: string) => boolean = (state: State, runnitId: string): boolean => {
	const unlimitedRunnits: boolean = !!(
		state.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ||
		_get(state.user, 'clubInfo.limit.unlimitedRunnits')
	);
	if (unlimitedRunnits) return true; // never past limit
	return !!state.limitNumRunnitsMap[runnitId]; // true = within limit
};

/**
 * Surfaces auto top-up outcomes as snackbars the moment the live-bound balance account reflects
 * them — the balance jumping "magically" mid-generation must always be legible. Fires only when a
 * success/failure timestamp ADVANCES on an already-bound account (never on the initial bind).
 */
export const maybeNotifyAutoTopUpEvent = (commit: any, prevBalanceAccount: BalanceAccount, nextBalanceAccount: BalanceAccount): void => {
	/**
	 * Did this stamp move forward between two snapshots of the same account?
	 *
	 * A missing `prev` alongside a present `next` counts as an advance — that is the first success or
	 * failure the account has ever recorded. Anything else needs a strictly later moment, so a
	 * re-emitted identical snapshot (Vuefire re-fires on any write to the doc) never re-notifies.
	 */
	const stampAdvanced = (prev: DbTimestamp, next: DbTimestamp): boolean => {
		if (!next) return false;
		if (!prev) return true;
		return moment(next.toMillis()).isAfter(moment(prev.toMillis()));
	};

	try {
		const prevAutoTopUp: BalanceAccountAutoTopUp = (prevBalanceAccount && prevBalanceAccount.autoTopUp) || null;
		const nextAutoTopUp: BalanceAccountAutoTopUp = (nextBalanceAccount && nextBalanceAccount.autoTopUp) || null;
		if (!prevAutoTopUp || !nextAutoTopUp) return;

		if (stampAdvanced(prevAutoTopUp.lastSuccessAt, nextAutoTopUp.lastSuccessAt)) {
			const tokensPart: string = nextAutoTopUp.lastSuccessRunnitTokensAmount
				? `+${currencyTokens(hundredthsToTokens(nextAutoTopUp.lastSuccessRunnitTokensAmount))} tokens`
				: 'tokens added';
			const pricePart: string = nextAutoTopUp.lastSuccessPriceAmount
				? ` (${currency(centsToDollars(nextAutoTopUp.lastSuccessPriceAmount))})`
				: '';
			commit('updateSnackbar', {
				status: SNACKBAR_STATUS.SUCCESS,
				message: `Auto top-up: ${tokensPart}${pricePart}`,
				show: true,
				timeout: 10000,
			});
			return;
		}

		if (stampAdvanced(prevAutoTopUp.lastFailureAt, nextAutoTopUp.lastFailureAt)) {
			commit('updateSnackbar', {
				status: SNACKBAR_STATUS.ERROR,
				message: `Auto top-up failed: ${nextAutoTopUp.lastFailureMessage || 'the charge could not be completed'}`,
				show: true,
				timeout: 10000,
			});
		}
	} catch (e) {
		console.error('maybeNotifyAutoTopUpEvent error', e);
	}
};
